import endpoints from 'consts/endpoints';
import baseRequest from 'queries/baseRequest';
import { AoTypeEnum } from 'hooks/api/useActivationOrder';

export type BulkAoActionT =
  | 'CHANGE_REPAYMENT_DAY'
  | 'TURN_OFF_CAPITALIZATION_FOR_PERIOD'
  | 'CHANGE_GRACE_PERIOD'
  | 'TURN_OFF_COMMISSION'
  | 'CHANGE_PAYMENT_DAY'
  | 'TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO'
  | 'TURN_OFF_DEFAULT_INTEREST'
  | 'TURN_OFF_DEFAULT_INTEREST_FOR_PERIOD';

export const MIN_GRACE_PERIOD = 1;
export const MAX_GRACE_PERIOD = 10;
export const MIN_PAYMENT_DAY = 1;
export const MAX_PAYMENT_DAY = 26;

export const MAX_BULK_SELECTION = 50;

export type BulkAvailableResponseT = {
  available_actions: BulkAoActionT[];
};

export type BulkActionResponseT = {
  success?: boolean;
  action: BulkAoActionT;
  processed: number;
  order_ids: string[];
};

export type ChangeRepaymentDayPayloadT = {
  action: 'CHANGE_REPAYMENT_DAY';
  order_ids: string[];
  params: { repayment_date: string };
};

export type TurnOffCapitalizationPayloadT = {
  action: 'TURN_OFF_CAPITALIZATION_FOR_PERIOD';
  order_ids: string[];
  params: { date_start: string; date_end: string };
};

export type ChangeGracePeriodPayloadT = {
  action: 'CHANGE_GRACE_PERIOD';
  order_ids: string[];
  params: { grace_period: number };
};

export type TurnOffCommissionPayloadT = {
  action: 'TURN_OFF_COMMISSION';
  order_ids: string[];
  params: { commission_disable_date: string };
};

export type ChangePaymentDayPayloadT = {
  action: 'CHANGE_PAYMENT_DAY';
  order_ids: string[];
  params: { payment_days: { payment_day: number; apply_from_date: string }[] };
};

export type TurnOffCapitalizationUntilEndPayloadT = {
  action: 'TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO';
  order_ids: string[];
  params: { capitalization_disable_date: string };
};

export type TurnOffDefaultInterestPayloadT = {
  action: 'TURN_OFF_DEFAULT_INTEREST';
  order_ids: string[];
  params: { default_interest_disable_date: string };
};

export type TurnOffDefaultInterestForPeriodPayloadT = {
  action: 'TURN_OFF_DEFAULT_INTEREST_FOR_PERIOD';
  order_ids: string[];
  params: { di_period_disabled: { date_start: string; date_end: string }[] };
};

export type BulkActionPayloadT =
  | ChangeRepaymentDayPayloadT
  | TurnOffCapitalizationPayloadT
  | ChangeGracePeriodPayloadT
  | TurnOffCommissionPayloadT
  | ChangePaymentDayPayloadT
  | TurnOffCapitalizationUntilEndPayloadT
  | TurnOffDefaultInterestPayloadT
  | TurnOffDefaultInterestForPeriodPayloadT;

export type BulkSettingT = {
  key: string;
  labelKey: string;
  action: BulkAoActionT | null;
};

// Порядок пунктов как в макете; action=null — bulk action отсутствует в контракте
export const AO_BULK_SETTINGS: BulkSettingT[] = [
  { key: 'AO_DATE', labelKey: 'forms.activationOrder.bulk.settings.aoDate', action: null },
  {
    key: 'PAYMENT_DAY',
    labelKey: 'forms.activationOrder.bulk.settings.paymentDay',
    action: 'CHANGE_PAYMENT_DAY',
  },
  {
    key: 'REPAYMENT_DAY',
    labelKey: 'forms.activationOrder.bulk.settings.repaymentDay',
    action: 'CHANGE_REPAYMENT_DAY',
  },
  {
    key: 'GRACE_PERIOD',
    labelKey: 'forms.activationOrder.bulk.settings.gracePeriod',
    action: 'CHANGE_GRACE_PERIOD',
  },
  {
    key: 'TURN_OFF_COMMISSION',
    labelKey: 'forms.activationOrder.bulk.settings.turnOffCommission',
    action: 'TURN_OFF_COMMISSION',
  },
  {
    key: 'TURN_OFF_CAPITALIZATION_FOR_PERIOD',
    labelKey: 'forms.activationOrder.bulk.settings.turnOffCapitalizationForPeriod',
    action: 'TURN_OFF_CAPITALIZATION_FOR_PERIOD',
  },
  {
    key: 'TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO',
    labelKey: 'forms.activationOrder.bulk.settings.turnOffCapitalizationUntilEnd',
    action: 'TURN_OFF_CAPITALIZATION_UNTIL_END_OF_AO',
  },
  {
    key: 'TURN_OFF_DEFAULT_INTEREST',
    labelKey: 'forms.activationOrder.bulk.settings.turnOffDefaultInterest',
    action: 'TURN_OFF_DEFAULT_INTEREST',
  },
  {
    key: 'TURN_OFF_DEFAULT_INTEREST_PERIOD',
    labelKey: 'forms.activationOrder.bulk.settings.turnOffDefaultInterestPeriod',
    action: 'TURN_OFF_DEFAULT_INTEREST_FOR_PERIOD',
  },
];

export const fetchBulkAvailableActions = (companyId: string, orderIds: string[]) =>
  baseRequest<BulkAvailableResponseT>({
    path: endpoints.getBulkAvailableActionsUrl(companyId),
    suppressErrorNotificationForStatuses: [400, 422],
    options: {
      method: 'POST',
      body: { order_ids: orderIds },
    },
  });

export const executeBulkAction = (companyId: string, payload: BulkActionPayloadT) =>
  baseRequest<BulkActionResponseT>({
    path: endpoints.executeBulkActionUrl(companyId),
    suppressErrorNotificationForStatuses: [400, 404, 422, 500],
    options: {
      method: 'POST',
      body: payload,
    },
  });

export const AO_TYPE_FILTER_OPTIONS = [
  { key: 'AR', legacyTypes: [AoTypeEnum.AVAILABLE_RECEIVABLES] },
  { key: 'FR', legacyTypes: [AoTypeEnum.FUTURE_RECEIVABLES, AoTypeEnum.FUTURE] },
  { key: 'CLASSIC', legacyTypes: [AoTypeEnum.CLASSIC] },
];

export const AO_STATUS_FILTER_OPTIONS = ['NEW', 'ACTIVE', 'FUNDED', 'REPAID', 'ARCHIVE', 'DELETED'];

export const matchesAoFilters = (
  row: { ao_type?: string; status?: string },
  typeFilters: string[],
  statusFilters: string[],
) => {
  const typeMatch =
    typeFilters.length === 0 ||
    typeFilters.some((filter) => {
      const option = AO_TYPE_FILTER_OPTIONS.find(({ key }) => key === filter);

      return Boolean(option?.legacyTypes.includes(row.ao_type as AoTypeEnum));
    });

  const statusMatch = statusFilters.length === 0 || statusFilters.includes(String(row.status || '').toUpperCase());

  return typeMatch && statusMatch;
};
